import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from '@lims/shared-validation';

/**
 * Forms validated by the SHARED Zod schemas.
 *
 * The schema in `@lims/shared-validation` is the one the API's validation pipe
 * uses. The frontend imports the same object, so the two cannot drift: a rule
 * tightened on the server tightens here in the same commit, and a field the
 * server rejects can no longer be silently accepted by the form.
 *
 * The frontend check is a COURTESY that saves a round trip. It is not a
 * security boundary — the backend re-validates every request regardless
 * (CLAUDE.md section 5.1), which is why a caller bypassing this form entirely
 * gets the same rejection.
 *
 * MESSAGES ARE NOT TAKEN FROM ZOD. Zod's own messages are English strings
 * generated by the library. Each issue is mapped to a translation KEY by its
 * Zod issue code, in the same way the API maps failures to error codes, so the
 * user reads their own language and adding a third one needs no code change.
 */

export type FieldErrors<Values> = Partial<Record<keyof Values & string, string>>;

/**
 * Zod issue code to catalogue key.
 *
 * Anything unmapped falls back to `validation.invalid`, which is honest: a
 * generic "this value is not acceptable" beats an untranslated English
 * sentence from a library.
 */
export const ISSUE_KEYS: Readonly<Record<string, string>> = {
  invalid_type: 'validation.required',
  too_small: 'validation.tooShort',
  too_big: 'validation.tooLong',
  invalid_string: 'validation.invalidFormat',
  invalid_enum_value: 'validation.invalidChoice',
  custom: 'validation.invalid',
};

export interface UseZodFormOptions<Schema extends z.ZodTypeAny> {
  readonly schema: Schema;
  readonly initialValues: z.input<Schema>;
  /** Called with the PARSED values once the schema accepts them. */
  readonly onSubmit: (values: z.output<Schema>) => void | Promise<void>;
}

export interface ZodForm<Values> {
  readonly values: Values;
  readonly errors: FieldErrors<Values>;
  /** True once a submit has been attempted; until then no field shows red. */
  readonly hasSubmitted: boolean;
  readonly isSubmitting: boolean;
  setField<Field extends keyof Values & string>(
    field: Field,
    value: Values[Field],
  ): void;
  setValues(values: Values): void;
  /** The message for a field, or undefined. Only after a submit attempt. */
  errorFor(field: keyof Values & string): string | undefined;
  submit(): Promise<void>;
  reset(values?: Values): void;
}

export function useZodForm<Schema extends z.ZodTypeAny>({
  schema,
  initialValues,
  onSubmit,
}: UseZodFormOptions<Schema>): ZodForm<z.input<Schema>> {
  type Values = z.input<Schema>;

  const { t } = useTranslation();
  const [values, setValuesState] = useState<Values>(initialValues);
  const [errors, setErrors] = useState<FieldErrors<Values>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const translateIssues = useCallback(
    (issues: readonly z.ZodIssue[]): FieldErrors<Values> => {
      const next: FieldErrors<Values> = {};
      for (const issue of issues) {
        const field = issue.path[0];
        if (typeof field !== 'string') continue;
        // The first issue per field wins: showing three complaints about one
        // input at once is noise, not help.
        if (next[field as keyof Values & string]) continue;
        const key = ISSUE_KEYS[issue.code] ?? 'validation.invalid';
        next[field as keyof Values & string] = t(key, {
          defaultValue: t('validation.invalid'),
        });
      }
      return next;
    },
    [t],
  );

  const setField = useCallback(
    <Field extends keyof Values & string>(field: Field, value: Values[Field]) => {
      setValuesState((current) => ({ ...current, [field]: value }));
      // Clear this field's complaint as soon as it is edited; re-validation on
      // the next submit decides whether it comes back.
      setErrors((current) => {
        if (!current[field]) return current;
        const next = { ...current };
        delete next[field];
        return next;
      });
    },
    [],
  );

  const submit = useCallback(async () => {
    setHasSubmitted(true);
    const result = schema.safeParse(values);

    if (!result.success) {
      setErrors(translateIssues(result.error.issues));
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    try {
      await onSubmit(result.data as z.output<Schema>);
    } finally {
      // Always cleared, including when the submit threw: the button must not
      // be left spinning over a request that has already failed.
      setIsSubmitting(false);
    }
  }, [schema, values, translateIssues, onSubmit]);

  const reset = useCallback(
    (next?: Values) => {
      setValuesState(next ?? initialValues);
      setErrors({});
      setHasSubmitted(false);
    },
    [initialValues],
  );

  const errorFor = useCallback(
    (field: keyof Values & string) =>
      hasSubmitted ? errors[field] : undefined,
    [errors, hasSubmitted],
  );

  return useMemo(
    () => ({
      values,
      errors,
      hasSubmitted,
      isSubmitting,
      setField,
      setValues: setValuesState,
      errorFor,
      submit,
      reset,
    }),
    [
      values,
      errors,
      hasSubmitted,
      isSubmitting,
      setField,
      errorFor,
      submit,
      reset,
    ],
  );
}
